import { useQuery } from '@tanstack/react-query';
import { formatPkr, type TripStatus } from '@raahi/shared';
import { Shell } from '../layout/Shell.js';
import { StatusPill } from '../components/StatusPill.js';
import { Skeleton } from '../components/Skeleton.js';
import { EmptyState } from '../components/EmptyState.js';
import { tripHistory } from '../api/trips.js';

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('en-PK', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

function TripSkeleton() {
  return (
    <li className="card p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-2">
          <Skeleton className="h-3 w-24 block" />
          <Skeleton className="h-4 w-36 block" />
          <Skeleton className="h-3 w-28 block" />
        </div>
        <div className="space-y-2 text-right">
          <Skeleton className="h-4 w-16 block" />
          <Skeleton className="h-4 w-20 block" />
        </div>
      </div>
    </li>
  );
}

export function HistoryPage() {
  const history = useQuery({ queryKey: ['history'], queryFn: tripHistory });

  return (
    <Shell>
      <div className="px-4 pt-[max(env(safe-area-inset-top),16px)]">
        <h1 className="font-display text-2xl font-bold">Trips</h1>
        <p className="text-sm text-muted">Your recent rides.</p>

        {history.isLoading ? (
          <ul className="mt-4 space-y-2">
            <TripSkeleton />
            <TripSkeleton />
            <TripSkeleton />
          </ul>
        ) : history.data && history.data.length > 0 ? (
          <ul className="mt-4 space-y-2">
            {history.data.map((t) => (
              <li key={t.id} className="card p-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-xs text-muted">{formatDate(t.requestedAt)}</p>
                    <p className="font-display text-base font-semibold">{t.pickup.label}</p>
                    <p className="text-sm text-muted">to {t.drop.label}</p>
                  </div>
                  <div className="text-right">
                    <p className="num font-display text-base font-semibold">{formatPkr(t.fare.total)}</p>
                    <StatusPill status={t.status as TripStatus} />
                  </div>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <EmptyState
            title="No trips yet"
            description="When you book your first ride, it will show up here."
          />
        )}
      </div>
    </Shell>
  );
}
